import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import PrimaryButton, { SecondaryButton } from '../../../../_common/button';
import {
  ActionType,
  useBookingContext,
} from '../../../../_common/context/BookingContext';
import { useCategoryContext } from '../../../../_common/context/CategoryContext';
import Modal from '../../../../_common/modal';
import { useScopedTranslation } from '../../../../_common/utils/hooks/useScopedTranslation';

export default function SwitchCategoryWatcher() {
  const { categories, category, onChangeCategory } = useCategoryContext();
  const [{ confirmForCategory }, dispatch] = useBookingContext();

  const newCategory = useMemo(() => {
    return categories.find(
      (c) => c.externalKey === confirmForCategory?.categoryExternalKey,
    );
  }, [categories, confirmForCategory]);

  function handleConfirm() {
    onChangeCategory(newCategory);
    dispatch({ type: ActionType.SHOW_CHANGE_CATEGORY, payload: null });
  }

  function handleCancel() {
    dispatch({ type: ActionType.SHOW_CHANGE_CATEGORY, payload: null });
  }

  if (confirmForCategory === null) {
    return null;
  }

  return (
    <ConfirmModal
      categoryLabel={newCategory?.label ?? ''}
      onCancel={handleCancel}
      onClose={handleCancel}
      onConfirm={handleConfirm}
      previousCategoryLabel={category?.label ?? ''}
    />
  );
}

interface ModalProps {
  previousCategoryLabel: string;
  categoryLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmModal(props: ModalProps) {
  const { categoryLabel, onCancel, onClose, onConfirm, previousCategoryLabel } =
    props;
  const { t } = useScopedTranslation();

  const createTranslation = useCallback(
    (key: string, category: string) =>
      t(`customer_form.change_category.${key}`, {
        category,
      }),
    [t],
  );

  const { cancelText, confirmText, extraText, switchText, warningText } =
    useMemo(
      () => ({
        warningText: createTranslation('warning', categoryLabel.toUpperCase()),
        confirmText: createTranslation('confirm', categoryLabel),
        switchText: createTranslation('switch', categoryLabel),
        cancelText: createTranslation('cancel', previousCategoryLabel),
        extraText: createTranslation('extra', categoryLabel),
      }),
      [createTranslation, previousCategoryLabel, categoryLabel],
    );

  return (
    <Modal onClose={onClose} title="" shouldHideClose>
      <div>{warningText}</div>
      <div>{confirmText}</div>
      <div className="px-12 py-6 w-full space-y-4">
        <PrimaryButton
          onClick={onConfirm}
          style={{ width: '100%', whitespace: 'nowrap' }}
        >
          {switchText}
        </PrimaryButton>
        <SecondaryButton
          onClick={onCancel}
          style={{ width: '100%', whitespace: 'nowrap' }}
        >
          {cancelText}
        </SecondaryButton>
      </div>
      <div>{extraText}</div>
    </Modal>
  );
}
